import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  DocumentSchema,
  DocumentCreateInputSchema,
  DocumentUpdateInputSchema,
  type Document,
  type DocumentCreateInput,
  type DocumentUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `document` (fichier conservé, org-wide).
 *
 * Réplique le PATRON `asset` : l'entrée est VALIDÉE par Zod
 * (`DocumentCreateInputSchema`) AVANT toute écriture Firestore. Les champs
 * standard sont remplis par le SDK, les requêtes `list` sont cadrées par orgId.
 *
 * Sécurité (cf. data-model) : `fileUrl` n'est jamais une URL publique
 * permanente. Le repository ne fait que persister le pointeur fourni ; c'est
 * l'app qui sert une URL signée de courte durée à la lecture.
 */

export type { DocumentCreateInput };

/** Nom de la collection Firestore de la famille document. */
export const DOCUMENTS_COLLECTION = 'documents';

/**
 * Repository typé pour la famille `document`.
 * Toute lecture est validée par `DocumentSchema`, toute écriture par
 * `DocumentCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface DocumentRepository {
  create(input: DocumentCreateInput): Promise<Document>;
  list(options?: ListOptions): Promise<Document[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Document | null>;
  update(id: string, patch: DocumentUpdateInput): Promise<Document>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Document>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Document>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Document>;
}

const documentConverter = createConverter<Document>((raw: DocumentData) => DocumentSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createDocumentRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): DocumentRepository {
  const col = collection(db, DOCUMENTS_COLLECTION).withConverter(documentConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Document>(db, col, DOCUMENTS_COLLECTION, getActorId);

  return {
    async create(input: DocumentCreateInput): Promise<Document> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore. (Même doctrine que le patron asset.)
      const fields = DocumentCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.mimeType !== undefined) optional['mimeType'] = fields.mimeType;
      if (fields.size !== undefined) optional['size'] = fields.size;
      if (fields.linkedRefs !== undefined) optional['linkedRefs'] = fields.linkedRefs;
      if (fields.version !== undefined) optional['version'] = fields.version;
      if (fields.visibility !== undefined) optional['visibility'] = fields.visibility;
      if (fields.expiresAt !== undefined) optional['expiresAt'] = fields.expiresAt;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        fileUrl: fields.fileUrl,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Document);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return DocumentSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async list(options?: ListOptions): Promise<Document[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Document | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    async update(id: string, patch: DocumentUpdateInput): Promise<Document> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = DocumentUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, DOCUMENTS_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`document ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
